import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import {
  FpLoggerNoiseLeakScoreActionGetData,
  FpLoggerNoiseLeakScoreActionGetDataError,
  FpLoggerNoiseLeakScoreActionGetDataSuccesses,
  FpLoggerNoiseLeakScoreActions,
  FpLoggerNoiseLeakScoreActionToggleChartFullscreen,
  FpLoggerNoiseLeakScoreActionType,
  FpLoggerNoiseLeakScoreActionUpdatePeriod,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-leak-score/services/fp-logger-noise-leak-score/fp-logger-noise-leak-score.actions';
import {
  IFpLoggerNoiseLeakScoreState,
  initialFpLoggerNoiseLeakScoreState,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-leak-score/services/fp-logger-noise-leak-score/fp-logger-noise-leak-score.state';
import { LoggerDeploymentChartDataApiService } from '@app/api/services/logger-deployment-chart-data-api.service';
import { DateRangeComparisonService } from '@app/core/date-range/services/date-range-comparison.service';
import { MonthsDateRangeTypeConfig } from '@app/core/date-range/configs/months-date-range-type.config';
import { convertDateChartDataInArray } from '@app/shared/utils/date-chart-data';
import {
  ILeakScoreDateChartDataRange,
  ILeakScoreDateChartDataValue,
} from '@app/api/interfaces/chart-data/date-leak-score';
import { DateChartDataService } from '@app/core/services/date-chart-data/date-chart-data.service';
import {
  ICombinedLeakScoreChartData,
  IEmptyLeakScoreDateChartDataRange,
  IEmptyLeakScoreDateChartDataValue,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-leak-score/interfaces/combined-leak-score-chart-data';
import { ICombinedDateChartData } from '@app/api/interfaces/chart-data/base-date-chart-data';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';

@Injectable()
export class FpLoggerNoiseLeakScoreStore
  extends ComponentStore<IFpLoggerNoiseLeakScoreState>
  implements IGcComponentStore<FpLoggerNoiseLeakScoreActions>
{
  public readonly data = inject<ILdLogger>(FLOATING_POPUP_DATA);
  protected readonly dateRangeService = inject(DateRangeComparisonService);
  protected readonly dateChartDataService = inject(DateChartDataService);
  protected readonly store = inject(Store);
  protected readonly chartDataApiService = inject(LoggerDeploymentChartDataApiService);
  public readonly popupService: FloatingPopupService = inject(FloatingPopupService);

  constructor() {
    super(initialFpLoggerNoiseLeakScoreState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly popupMainData$ = this.select(s => s.popupMainData, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly period$ = this.select(s => s.period, { equal });
  public readonly error$ = this.select(s => s.error, { equal });

  private mainProjectId = this.store.selectSignal(selectMainProjectId);

  public readonly loading$ = this.select(s => s.loading, { equal });

  public readonly isChartFullscreenActive$ = this.select(s => s.isChartFullscreenActive, { equal });

  public dispatch = this.effect((action$: Observable<FpLoggerNoiseLeakScoreActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpLoggerNoiseLeakScoreActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpLoggerNoiseLeakScoreActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpLoggerNoiseLeakScoreActionType.GetDataError: {
            this.reducerGetDataError(action);
            break;
          }
          case FpLoggerNoiseLeakScoreActionType.UpdatePeriod: {
            this.reducerUpdatePeriod(action);
            this.effectUpdatePeriod();
            break;
          }
          case FpLoggerNoiseLeakScoreActionType.ToggleChartFullscreen: {
            this.reducerToggleChartFullscreen();
            this.effectToggleChartFullscreen(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({
      loading: true,
      error: null,
    });
  }

  private reducerGetDataSuccesses(action: FpLoggerNoiseLeakScoreActionGetDataSuccesses) {
    const state = this.get();

    this.patchState({
      date: action.date,
      cache: {
        ...state.cache,
        dateRange:
          action.cacheDateRange === null || !!action.cacheDateRange ? action.cacheDateRange : state.cache.dateRange,
        data: action.cacheData === null || !!action.cacheData ? action.cacheData : state.cache.data,
      },
      popupMainData: action.popupMainData,
      data: action.data,
      loading: false,
    });
  }

  private reducerGetDataError(action: FpLoggerNoiseLeakScoreActionGetDataError) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: null,
      error: action.error,
      loading: false,
    });
  }

  private reducerUpdatePeriod(action: FpLoggerNoiseLeakScoreActionUpdatePeriod) {
    this.patchState({
      period: action.period,
    });
  }

  private reducerToggleChartFullscreen() {
    const state = this.get();
    this.patchState({
      isChartFullscreenActive: !state.isChartFullscreenActive,
    });
  }

  private effectGetData(action: FpLoggerNoiseLeakScoreActionGetData) {
    const { deployment } = action.popupMainData || {};
    const mainProjectId = this.mainProjectId();
    const state = this.get();

    if (!mainProjectId || !deployment) {
      this.dispatch({
        type: FpLoggerNoiseLeakScoreActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: null,
      });

      return;
    }

    const monthsPeriod = MonthsDateRangeTypeConfig[state.period];

    const { isCacheInvalid, isCacheContainData, cacheDateRange, showDateRange, dateRangesForRequest } =
      this.dateChartDataService.manageCacheDate(state.cache.dateRange, action.date, monthsPeriod);

    if (isCacheContainData) {
      const data = this.dateChartDataService.filterDataAccordingToTheRange<ICombinedLeakScoreChartData>(
        showDateRange,
        state.cache.data || [],
        el => el.leakScore === null
      );

      this.dispatch({
        type: FpLoggerNoiseLeakScoreActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data,
      });

      return;
    }

    const requests = dateRangesForRequest.map(dRange =>
      this.chartDataApiService.getDateLeakScore(mainProjectId, deployment.deploymentId, {
        ...dRange,
        includeRelatedDeployments: true,
      })
    );

    const oldCacheData = isCacheInvalid ? [] : state.cache.data || [];

    forkJoin(requests)
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpLoggerNoiseLeakScoreActionType.GetDataError,
            date: action.date,
            popupMainData: action.popupMainData,
            error,
          });
          return EMPTY;
        })
      )
      .subscribe(requestsData => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const arrayOfData = (requestsData as any[]).map(el =>
          convertDateChartDataInArray<ILeakScoreDateChartDataValue, ILeakScoreDateChartDataRange>(el.data)
        );

        const responseData: ICombinedLeakScoreChartData[] = arrayOfData.reduce(
          (acc, convertedData) => acc.concat(convertedData),
          []
        );

        const rawCacheData = [...oldCacheData, ...responseData];

        const cacheData = this.dateChartDataService.fillAllEmptyDateDataAccordingToTheRange(
          cacheDateRange,
          rawCacheData,
          (date, prev, next) => {
            const lastExist = prev ?? next;

            const emptyObj: ICombinedDateChartData<
              IEmptyLeakScoreDateChartDataValue,
              IEmptyLeakScoreDateChartDataRange
            > = {
              date,
              deploymentId: deployment.deploymentId,
              deployed: deployment.deployTime,
              collected: deployment.deployTime,
              serialNo: deployment.serialNo,
              leakScore: null,
              warningThreshold: lastExist?.warningThreshold || null,
              criticalThreshold: lastExist?.criticalThreshold || null,
            };

            return emptyObj;
          }
        );

        const data = this.dateChartDataService.filterDataAccordingToTheRange<ICombinedLeakScoreChartData>(
          showDateRange,
          cacheData || [],
          el => el.leakScore === null // filter for ignoring when startDate or endDate equal null
        );

        this.dispatch({
          type: FpLoggerNoiseLeakScoreActionType.GetDataSuccesses,
          date: action.date,
          cacheData,
          cacheDateRange,
          popupMainData: action.popupMainData,
          data,
        });
      });
  }

  private effectUpdatePeriod() {
    const state = this.get();

    this.dispatch({
      type: FpLoggerNoiseLeakScoreActionType.GetData,
      date: state.date,
      popupMainData: state.popupMainData,
    });
  }

  private effectToggleChartFullscreen(action: FpLoggerNoiseLeakScoreActionToggleChartFullscreen) {
    if (!action.synchronizeWithPopupId) return;

    const state = this.get();

    if (state.isChartFullscreenActive) {
      this.popupService.enterFullscreen(action.synchronizeWithPopupId, false);
    } else {
      this.popupService.exitFullscreen(action.synchronizeWithPopupId, false);
    }
  }
}
